/* eslint-disable import/order */

import App from '@/App.vue'
import ability from '@/plugins/casl/ability'
import i18n from '@/plugins/i18n'
import layoutsPlugin from '@/plugins/layouts'
import vuetify from '@/plugins/vuetify'
import { loadFonts } from '@/plugins/webfontloader'
import { reportErrorVue, reportWarningVue, reportError } from '@/plugins/misc'
import router from '@/router'
import '@/styles/styles.scss'
import { abilitiesPlugin } from '@casl/vue'
import '@core/scss/index.scss'
import { createPinia , storeToRefs } from 'pinia'
import { createApp } from 'vue'
import axios from "@axios"
import VueApexCharts from "vue3-apexcharts"
import { useUsersStore, useCmsContentStore } from '@/stores'
import { vuetifyProTipTap } from './plugins/tiptap'
import * as Sentry from "@sentry/vue"

loadFonts()
const app = createApp(App)

// RM-233 item 4 (with RM-300): events are tagged with the environment they
// came from and the build that produced them, and carry no member details by
// default (no IP, no cookies). The environment is read from the host the app is
// served on (the client and API share it); a local dev build reports nothing.
const sentryEnvironment = (() => {
  const host = String(window.location.hostname || '')
  if (!host || /localhost|127\.0\.0\.1/.test(host)) return 'local'
  if (/^staging/.test(host)) return 'staging'
  if (/^dev\./.test(host)) return 'development'
  return 'production'
})()

Sentry.init({
  app,
  dsn: sentryEnvironment === 'local' ? undefined : "https://2d1415ccff2240c87cf1248f6e765f72@o4510070675865600.ingest.us.sentry.io/4510587544928256",
  environment: sentryEnvironment,
  release: `rm-web-client@${import.meta.env.VITE_BUILD_NUMBER || 'unknown'}`,
  sendDefaultPii: false,
  integrations: [
    // Replays are error-only (replaysOnErrorSampleRate below); masking stays on
    // (the default) so member text and inputs are never recorded.
    Sentry.replayIntegration()
  ],

  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

app.config.errorHandler = (err, instance, info) => {
  Sentry.captureException(err)
  reportErrorVue(err, instance, info)
}

app.config.warnHandler = (msg, instance, trace) => {
  reportWarningVue(msg, instance, trace)
}

window.onerror = function (msg, url, lineNo, columnNo, error) {
  reportError("window.onerror", msg, url, lineNo, columnNo, error)
}

app.use(vuetify)
app.use(createPinia())
app.use(router)
app.use(layoutsPlugin)
app.use(i18n)
app.use(VueApexCharts)
app.use(vuetifyProTipTap)

// Extract acl to another file like vitesse
app.use(abilitiesPlugin, ability, {
  useGlobalProperties: true,
})

const usersStore = useUsersStore()
let appVersion = null
let isRunningServiceWorker = null;
({ appVersion, isRunningServiceWorker } = storeToRefs(usersStore))

//watches for server version changes and updates local storage when needed.
//reload is handled by service worker when isRunningServiceWorker is true
axios.interceptors.response.use(
  response => {
    const rmVersion = response.headers['rm-version']
    if (rmVersion && rmVersion !== appVersion.value) {
      console.log("new version detected: " + rmVersion + " - old:" + appVersion.value)

      let dontReload = false
      if (appVersion.value == "0.0.0") {
        //first time loading the app
        dontReload = true
      }
      usersStore.setAppVersion(rmVersion)

      //pwa takes care of its own reloads
      if (!isRunningServiceWorker.value && !dontReload) {
        window.location.reload(true)
      }
    }

    // Always return the response to the next handler in chain.
    return response
  },
  error => {
    // Check if the response was a 401 Unauthorized error.
    if (error.response && error.response.status === 401) {
      console.log('401 Unauthorized error detected')

      //logging out the user
      localStorage.removeItem('userData')
      localStorage.removeItem('accessToken')
      localStorage.removeItem('jwt')

      // Don't remove simulation flag — the exit button must remain visible
      // so the admin can exit simulation mode. The httpOnly cookie is still
      // intact and will authenticate the exit-simulation API call.
      if (!localStorage.getItem('simulation')) {
        // location.href = "/auth/logout?loc=1"
      }
    }

    // Return any other errors to the next handler in chain.
    return Promise.reject(error)
  }
)

app.mount('#app')

// Load CMS content from DB and merge into i18n (non-blocking)
const cmsContentStore = useCmsContentStore()
cmsContentStore.loadContent(i18n)
